import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, FieldWrapper, TimeMaskedInput } from 'components';
import validations from 'lib/Validations';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Site, updateBusinessDayStartRequest } from 'store/Site';
import { IStore } from 'store/types';
import { ISite } from 'types';
import { formatTimezone } from 'utils/date';
import { ActionSection } from '../../../styles';

interface IBusinessDayStartFormProps {
  site: ISite;
  onClose(): void;
}

const getTimezoneLabel = (tz?: string) => (tz ? `(${formatTimezone(tz)})` : undefined);

const validationSchema = Yup.object().shape({
  businessDayStart: validations.timeValidationSchema,
});

export const BusinessDayStartForm: React.FC<IBusinessDayStartFormProps> = ({ site, onClose }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.UPDATE_BUSINESS_DAY_START_REQUEST));
  const { businessDayStart, timezone } = site?.settings || {};

  const handleSubmit = (values: { businessDayStart: string }) => {
    dispatch(updateBusinessDayStartRequest(site.id, values.businessDayStart, onClose));
  };

  return (
    <Formik initialValues={{ businessDayStart: businessDayStart || '' }} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ dirty, errors, isValid, touched, getFieldProps, setFieldValue }) => {
        const hasFieldError = Boolean(touched.businessDayStart && errors.businessDayStart);
        return (
          <Form data-cy="business-day-start-form">
            <FieldWrapper label="Time" subLabel={getTimezoneLabel(timezone)} data-cy="business-day-start" hasError={hasFieldError} message={hasFieldError ? errors.businessDayStart : ''}>
              <TimeMaskedInput {...getFieldProps('businessDayStart')} onChange={(val) => setFieldValue('businessDayStart', val)} disabled={isLoading} />
            </FieldWrapper>

            <ActionSection $marginTop={0}>
              <Button label="Cancel" data-cy="cancel-button" appearance="white" size="small" onClick={onClose} disabled={isLoading} />
              <Button type="submit" label="Save" data-cy="save-business-day-start-button" size="small" isProcessing={isLoading} disabled={isLoading || !dirty || !isValid} />
            </ActionSection>
          </Form>
        );
      }}
    </Formik>
  );
};
